import React, { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  DrawerBody, Flex, Tab, TabList, TabPanel,
  TabPanels, Tabs
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import CancelAndSaveButtons from '../../../../../../components/CancelAndSaveButtons';
import TrayHeader from '../../../../../../components/Tray/TrayHeader';
import {
  adminPageUpdateUserInfo,
  selectAdminPageNewUserData,
  selectAdminPageUsers,
  selectAdminUser
} from '../../../../../../redux/modules/adminPage';
import { updateTrayProps } from '../../../../../../redux/modules/tray';
import { removePhoneNumberFormat } from '../../../../../../utils/formatPhoneUtil';
import InviteUser from '../UserForm/InviteUser';
import UserInfo from '../UserForm/UserInfo';
import UserPermissions from '../UserForm/UserPermissions';

const EditUserForm = ({ companyId, onClose, userId }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAdminUser(userId));
  const listUsers = useSelector(selectAdminPageUsers);
  const newUserData = useSelector(selectAdminPageNewUserData);

  let userSupervisor;
  if (user.supervisorId) {
    userSupervisor = listUsers.find(u => u.userId === user.supervisorId);

    if (userSupervisor) {
      userSupervisor = {
        name: `${userSupervisor?.userFirstName} ${userSupervisor?.userLastName}`,
        id: userSupervisor.userId
      };
    }
  }

  const useFormMethods = useForm({
    defaultValues: {
      firstName: user.userFirstName,
      lastName: user.userLastName,
      email: user?.userEmail,
      phone: user?.userPhone,
      externalId: user?.externalId,
      supervisor: userSupervisor?.id
    }
  });
  const { handleSubmit, control } = useFormMethods;
  const { isDirty, isValid } = useFormState({ control });

  useEffect(() => {
    dispatch(updateTrayProps({ formIsDirty: isDirty }));
  }, [isDirty]);

  const onSubmit = async data => {
    await dispatch(adminPageUpdateUserInfo(user.userId, {
      user_first_name: data.firstName,
      user_last_name: data.lastName,
      user_phone: removePhoneNumberFormat(data?.phone),
      supervisor_id: newUserData?.supervisorId || data.supervisorId,
      external_id: data.externalId
    }));

    setTimeout(onClose, 200);
  };

  return (
    <>
      <TrayHeader bgColor="primaryScale.300" fontColor="white" title="Edit User" />
      <DrawerBody>
        <Tabs isFitted height="100%" p={0}>
          <TabList borderBottomColor="greyScale.400" borderBottomWidth="1.5px" mb={9} p={0}>
            <Tab
              _focus={{ outline: 'none' }}
              _selected={{
                color: 'greyScale.900',
                borderBottom: '2px solid',
                borderBottomColor: 'success.primary.600',
                fontWeight: '600'
              }}
              color={!isValid ? 'error.primary.600' : 'initial'}
              pb={4}
              textStyle="bodyMd"
            >
              Information
            </Tab>
            <Tab
              _focus={{ outline: 'none' }}
              _selected={{
                color: 'greyScale.900',
                borderBottom: '2px solid',
                borderBottomColor: 'success.primary.600',
                fontWeight: '600'
              }}
              pb={4}
              textStyle="bodyMd"
            >
              Permissions
            </Tab>
            <Tab
              _focus={{ outline: 'none' }}
              _selected={{
                color: 'greyScale.900',
                borderBottom: '2px solid',
                borderBottomColor: 'success.primary.600',
                fontWeight: '600'
              }}
              pb={4}
              textStyle="bodyMd"
            >
              Resend Invite
            </Tab>
          </TabList>
          <TabPanels height="100%" p={0}>
            <TabPanel height="100%" p={0}>
              <form id="editUserForm" onSubmit={handleSubmit(onSubmit)}>
                <UserInfo
                  defaultSelected={userSupervisor?.id}
                  isDisabled={!!user.userEmail}
                  useFormMethods={useFormMethods}
                  user={user}
                />
                <Flex justifyContent="center" mt={8}>
                  <CancelAndSaveButtons formId="editUserForm" onClose={onClose} />
                </Flex>
              </form>
            </TabPanel>
            <TabPanel height="100%" overflowY="auto" p={0}>
              <UserPermissions companyId={companyId} user={user} />
            </TabPanel>
            <TabPanel height="100%" p={0}>
              <InviteUser user={user} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </>
  );
};

EditUserForm.propTypes = {
  onClose: PropTypes.func,
  userId: PropTypes.number,
  companyId: PropTypes.number.isRequired
};

export default EditUserForm;
